import { getSupabase } from "../supabase";

import { summarizeRun, formatDuration } from "./runStages.mjs";
import type { RunSummary, RunStageEntry } from "./runStages.mjs";

export type { RunSummary, RunStageEntry };
export { formatDuration };

/**
 * MER-489: data layer for the admin Runs viewer.
 *
 * Reads `pipeline_runs` directly (staff-readable as of migration 0078;
 * see that file for the RLS gap it closes). No FastAPI round trip: the
 * MER-263 `/runs/{id}/logs` endpoint lives on the droplet and is not
 * reachable from a browser session, but everything the ticket asks
 * for ("did last night's run work, and why not") is already captured
 * in `pipeline_runs.stages` by the orchestrator (pipeline/
 * orchestrator.py, pipeline/run_record.py), so this reads that instead
 * of standing up a proxy.
 */

interface PipelineRunRow {
  id: string;
  customer_id: string;
  status: string;
  triggered_by: string | null;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  stages: Record<string, unknown> | null;
  error: string | null;
}

export interface RunFilters {
  customerId?: string;
  status?: "running" | "completed" | "failed";
  /** ISO date (yyyy-mm-dd), inclusive, matched against started_at. */
  since?: string;
  /** ISO date (yyyy-mm-dd), inclusive, matched against started_at. */
  until?: string;
}

const SELECT_COLUMNS =
  "id, customer_id, status, triggered_by, started_at, completed_at, duration_seconds, stages, error";

export async function listRuns(filters: RunFilters = {}, limit = 100): Promise<RunSummary[]> {
  const supabase = getSupabase();
  let query = supabase.from("pipeline_runs").select(SELECT_COLUMNS);

  if (filters.customerId) query = query.eq("customer_id", filters.customerId);
  if (filters.status) query = query.eq("status", filters.status);
  if (filters.since) query = query.gte("started_at", `${filters.since}T00:00:00Z`);
  if (filters.until) query = query.lte("started_at", `${filters.until}T23:59:59Z`);

  const { data, error } = await query.order("started_at", { ascending: false }).limit(limit);
  if (error) throw new Error(`listRuns failed: ${error.message}`);
  const rows = (data as PipelineRunRow[] | null) ?? [];
  return rows.map((r) => summarizeRun(r as unknown as Record<string, unknown>));
}

export async function getRun(id: string): Promise<RunSummary | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("pipeline_runs")
    .select(SELECT_COLUMNS)
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(`getRun failed: ${error.message}`);
  if (!data) return null;
  return summarizeRun(data as unknown as Record<string, unknown>);
}
